import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import { formatInTimezone } from '@/utils/date'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'

const WorkoutRow = ({ workout, user, enrolledWorkoutIds, compact }) => {
  const { putpostRequest } = useQuery()
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')

  const [enrolled, setEnrolled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [enrollmentType, setEnrollmentType] = useState(null)

  useEffect(() => {
    if (workout === null) { return }
    setEnrolled(enrolledWorkoutIds.includes(workout.id))
  }, [enrolledWorkoutIds])

  const toggleEnroll = (type) => {
    setLoading(true)
    const data = {
      enrollment_type: type
    }
    
    putpostRequest(`/api/v3/workouts/${workout.id}/enroll`, 'POST', data, (err, jsonData) => {
      setLoading(false)
      if (err) { console.log('toast errors'); return }

      setEnrolled(!enrolled)
      setEnrollmentType(type)
      
      if (!enrolled) {
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-green-100 text-green-600">Enrolled!</p>
          <p className="mt-1 text-sm dark:text-green-300 text-green-600">You're enrolled in this workout. Check your email for details.</p>
        </div>)
      } else { 
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-yellow-100 text-yellow-600">Enrollment canceled</p>
          <p className="mt-1 text-sm dark:text-gray-200 text-gray-900">You are no longer enrolled in this workout.</p>
        </div>)
      }
    })
  }

  const getMemberPrice = (type) => {
    return type === 'live' ? workout.liveSessionMemberPrice : workout.asyncSessionMemberPrice
  }

  const getNonMemberPrice = (type) => {
    return type === 'live' ? workout.liveSessionNonmemberPrice : workout.asyncSessionNonmemberPrice
  }

  const getCapacity = (type) => {
    return type === 'live' ? workout.liveCapacity : workout.asyncCapacity
  }

  const getEnrollmentCount = (type) => {
    return type === 'live' ? workout.liveEnrollmentCount : workout.asyncEnrollmentCount
  }

  const spotsRemaining = (type) => {
    const capacity = getCapacity(type)
    const enrollmentCount = getEnrollmentCount(type)
    return capacity > 0 ? capacity - enrollmentCount : null
  }

  const hasAvailableSpots = () => {
    const liveSpots = spotsRemaining('live')
    const asyncSpots = spotsRemaining('async')
    return (liveSpots && liveSpots > 0) || (asyncSpots && asyncSpots > 0)
  }

  return <div className="group flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-cccpurple dark:hover:border-cccpurple-alt">
    {!compact && <a href={`/workouts/${workout.uuid}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 relative overflow-hidden">
      <div className="aspect-w-16 aspect-h-9 relative">
        <img className="object-cover group-hover:scale-105 transition-transform duration-300" src={`${workout.imageUrl}?aspect=16:9&width=420`} alt={workout.name} />
      </div>
      {workout.status === 'started' && (
        <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse z-10">
          Live Now
        </div>
      )}
      <div className="absolute top-2 left-2 bg-cccpurple text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
        Pro Take
      </div>
    </a>}
    <div className="flex-1 p-6 flex flex-col justify-between">
      <div className="flex-1">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-cccpurple dark:group-hover:text-cccpurple-alt transition-colors">{workout.name}</h3>
        </div>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mb-3">
          <svg className="w-4 h-4 mr-1.5 text-cccpurple dark:text-cccpurple-alt" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {workout.status === 'started' ? (
            <span className="text-green-600 dark:text-green-400 font-medium">Workout has started!</span>
          ) : (
            <time dateTime={workout.startsAtUtc}>
              {formatInTimezone(workout.startsAtUtc, user.timezone, 'EEE MMM d, yyyy h:mmaaa')} • {workout.lengthOfWorkout} min
            </time>
          )}
        </div>
        <div className="block">
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3" dangerouslySetInnerHTML={{ __html: workout.description }}></p>
        </div>
      </div>
    </div>
    <div className="bg-gradient-to-r from-cccpurple to-cccpurple-alt p-4">
      <div className="flex items-center justify-between">
        <a 
          href={`/workouts/${workout.id}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="inline-flex items-center px-4 py-2 bg-white text-cccblue rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors"
        >
          View Details
          <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  </div>
}

export default WorkoutRow
